import { Validate, IsNotEmpty } from 'class-validator';
import { WordsAreValid } from './wordsValidator';

export enum MatcherType {
  Contains = 'contains',
  Exact = 'exact',
  Regex = 'regex',
  Words = 'words',
}

abstract class Matcher {
  caseSensitive: boolean;
  type: MatcherType;

  setCaseSensitive(isSensitive: boolean): void {
    this.caseSensitive = isSensitive;
  }

  abstract setValue(v: string): void;
  abstract getValue(): string;
  abstract toJs(): object;

  constructor({ caseSensitive, type }: { caseSensitive: boolean; type: MatcherType }) {
    this.caseSensitive = caseSensitive;
    this.type = type;
  }
}

abstract class ValueMatcher extends Matcher {
  @IsNotEmpty({ message: 'Please specify a match value' })
  values: string;

  constructor({
    caseSensitive = false,
    type,
    values = '',
  }: {
    caseSensitive: boolean;
    type: MatcherType;
    values: string[] | string;
  }) {
    super({ caseSensitive, type });
    this.values = Array.isArray(values) ? values.join(', ') : values;
  }

  getValue() {
    return this.values;
  }

  setValue(value) {
    this.values = value;
  }

  toJs() {
    return {
      [this.type]: {
        caseSensitive: this.caseSensitive,
        values: this.values,
      },
    };
  }
}

export class ContainsValueMatcher extends ValueMatcher {
  constructor(attr = {} as any) {
    super({ ...attr, type: MatcherType.Contains });
  }
}

export class ExactValueMatcher extends ValueMatcher {
  constructor(attr = {} as any) {
    super({ ...attr, type: MatcherType.Exact });
  }
}
export class WordsMatcher extends Matcher {
  @IsNotEmpty({ message: 'Please specify a match value' })
  @Validate(WordsAreValid)
  values: string;

  constructor(
    { caseSensitive = false, values = '' }: { caseSensitive: boolean; values: string[] | string } = {
      caseSensitive: false,
      values: '',
    }
  ) {
    super({ caseSensitive, type: MatcherType.Words });
    this.values = Array.isArray(values) ? values.join(', ') : values;
  }

  getValue() {
    return this.values;
  }

  setValue(value) {
    this.values = value;
  }

  toJs() {
    return {
      [this.type]: {
        caseSensitive: this.caseSensitive,
        values: this.values,
      },
    };
  }
}

export class RegexMatcher extends Matcher {
  @IsNotEmpty({ message: 'Please specify a match value' })
  expression: string;

  constructor(
    { caseSensitive = false, expression = '' }: { caseSensitive: boolean; expression: string } = {
      caseSensitive: false,
      expression: '',
    }
  ) {
    super({ caseSensitive, type: MatcherType.Regex });
    this.expression = expression;
  }

  getValue() {
    return this.expression;
  }

  setValue(value) {
    this.expression = value;
  }

  toJs() {
    return {
      [this.type]: {
        caseSensitive: this.caseSensitive,
        expression: this.expression,
      },
    };
  }
}

export class ConditionMatcher {
  matcher: RegexMatcher | WordsMatcher | ValueMatcher;

  // eslint-disable-next-line
  constructor(attr = { contains: {} } as any) {
    if (attr.contains !== undefined) {
      this.matcher = new ContainsValueMatcher(attr.contains);
    }

    if (attr.exact !== undefined) {
      this.matcher = new ExactValueMatcher(attr.exact);
    }

    if (attr.regex !== undefined) {
      this.matcher = new RegexMatcher(attr.regex);
    }

    if (attr.words !== undefined) {
      this.matcher = new WordsMatcher(attr.words);
    }
  }

  getCaseSensitive() {
    return !!this.matcher.caseSensitive;
  }

  setCaseSensitive(isCaseSensitive: boolean) {
    this.matcher.setCaseSensitive(isCaseSensitive);
  }

  getValue() {
    return this.matcher.getValue();
  }

  getValuePath(): string {
    switch (this.getType()) {
      case MatcherType.Contains:
        return 'contains.values';
      case MatcherType.Exact:
        return 'exact.values';
      case MatcherType.Regex:
        return 'regex.expression';
      case MatcherType.Words:
        return 'words.values';
    }
  }

  setValue(value) {
    this.matcher.setValue(value);
  }

  getType() {
    return this.matcher.type;
  }

  setType(type) {
    if (type === this.getType()) return;

    const matcher = this.matcher;
    const currVal = matcher.getValue();
    const caseSensitive = matcher.caseSensitive;

    if (type === MatcherType.Regex && matcher.type !== MatcherType.Regex) {
      // convert value format from ValueMatcher to RegexMatcher
      this.matcher = new RegexMatcher({ caseSensitive, expression: currVal });
      return;
    }

    if (type === MatcherType.Contains) {
      this.matcher = new ContainsValueMatcher({ caseSensitive, values: currVal });
    } else if (type === MatcherType.Words) {
      this.matcher = new WordsMatcher({ caseSensitive, values: currVal });
    } else {
      this.matcher = new ExactValueMatcher({ caseSensitive, values: currVal });
    }
  }

  toJs(): object {
    return this.matcher.toJs();
  }
}

export default class StringMatcher extends ConditionMatcher {
  not?: ConditionMatcher;

  // eslint-disable-next-line
  constructor(attr = { contains: {} } as any) {
    super(attr);
    if (attr.not !== undefined) {
      this.not = new ConditionMatcher(attr.not);
    }
  }

  hasNot(): boolean {
    return !!this.not;
  }

  setNot(isNot: boolean) {
    if (isNot === this.hasNot()) return;

    if (isNot) {
      this.not = new ConditionMatcher(this.matcher.toJs());
      // @ts-ignore
      delete this.matcher;
    } else {
      this.matcher = (this.not as ConditionMatcher).matcher;
      delete this.not;
    }
  }

  getType() {
    return this.hasNot() ? this.not!.getType() : super.getType();
  }

  setType(type) {
    if (this.hasNot()) {
      this.not!.setType(type);
    } else {
      super.setType(type);
    }
  }

  getValue() {
    return this.hasNot() ? this.not!.getValue() : super.getValue();
  }

  getValuePath() {
    return this.hasNot() ? `not.${super.getValuePath()}` : super.getValuePath();
  }

  setValue(value) {
    if (this.hasNot()) {
      this.not!.setValue(value);
    } else {
      super.setValue(value);
    }
  }

  getCaseSensitive() {
    return this.hasNot() ? this.not!.getCaseSensitive() : super.getCaseSensitive();
  }

  setCaseSensitive(isCaseSensitive: boolean) {
    if (this.hasNot()) {
      this.not!.setCaseSensitive(isCaseSensitive);
    } else {
      super.setCaseSensitive(isCaseSensitive);
    }
  }

  toJs() {
    return this.hasNot() ? { not: this.not!.toJs() } : super.toJs();
  }
}
